import { useQuery } from '@apollo/client';
import { i18n } from '@lingui/core';
import { CircularProgress, Typography, useTheme } from '@mui/material';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import moment from 'moment';
import numeral from 'numeral';
import * as React from 'react';
import { Bar } from 'react-chartjs-2';
import { StatsPeriod } from '../../../../../../common/components/SelectTimeframe/SelectTimeframe';
import { formatAmount, formatDurationAsHMS, PERCENTAGE_FORMAT } from '../../../../../../common/formatting';
import { DeveloperDailyRevenueStat } from '../../Games/userDeveloperSubmission-graphql';
import { DeveloperDailyStat, MY_GAME_DAILY_STATS_QUERY, MyGameDailyStatsQuery, Submission } from '../submission-graphql';
import { DeveloperSubmissionStatsMetric, ClientStatsDeviceFilter } from './DeveloperSubmissionStats';
import { Tooltip as MuiTooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface DeveloperSubmissionStatsDailyBarProps {
  submission: Submission;
  deviceFilter: ClientStatsDeviceFilter;
  timeframeFilter: StatsPeriod;
  selectedMetric: DeveloperSubmissionStatsMetric;
}

const DeveloperSubmissionStatsDailyBar: React.FunctionComponent<DeveloperSubmissionStatsDailyBarProps> = ({
  submission,
  timeframeFilter,
  selectedMetric, // Destructure selectedMetric
  deviceFilter,
}) => {
  const theme = useTheme();

  // If timeframeFilter.period is 'ALL_TIME', limit the timeframe.options to 1 year.
  const safeTimeframeFilter = React.useMemo(() => {
    if (timeframeFilter.period === 'ALL_TIME') {
      return {
        ...timeframeFilter,
        options: {
          ...timeframeFilter.options,
          from: moment.utc().subtract(1, 'year').format('YYYY-MM-DD'),
        },
      };
    }
    return timeframeFilter;
  }, [timeframeFilter]);

  const { loading, data: dailyGameData } = useQuery<MyGameDailyStatsQuery>(MY_GAME_DAILY_STATS_QUERY, {
    variables: {
      slug: submission.slug,
      options: safeTimeframeFilter.options,
      // graph treats undefined as 'all'
      device: deviceFilter === 'all' ? undefined : deviceFilter,
    },
  });

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', paddingTop: theme.spacing(4) }}>
        <CircularProgress />
      </div>
    );
  }

  const getChartRawData = (): DeveloperDailyStat[] | DeveloperDailyRevenueStat[] => {
    if (!dailyGameData) {
      return [];
    }
    if (selectedMetric === 'revenue') {
      return [...dailyGameData.gameBySlug.dailyRevenueStats].reverse();
    }
    return dailyGameData.gameBySlug.dailyStats;
  };

  const getNotice = (rawData: DeveloperDailyStat[] | DeveloperDailyRevenueStat[]): string | null => {
    if (selectedMetric === 'revenue') {
      for (const stat of rawData as DeveloperDailyRevenueStat[]) {
        if (stat.revenueShareDaily.iap > 0) {
          return "This chart includes both ad revenue and in-game purchase revenue. Please note that due to Xsolla's daily exchange rate updates, in-game purchase revenue may fluctuate in the daily data. Therefore, we recommend comparing the full month's revenue only after receiving the self-billing invoice, which reflects the final, fixed amount following Xsolla's monthly closing.\n\nAdditionally, minor discrepancies may occur between the Xsolla UI (which uses UTC+3) and the Developer Portal (which uses UTC) due to timezone differences. If you are matching revenue figures between these platforms, make sure to account for this timezone shift to ensure accurate comparisons.";
        }
      }
    }
    return null;
  };

  const getYAxisLabel = (): string => {
    switch (selectedMetric) {
      case 'pageviews':
        return 'Total plays';
      case 'avgTimeOnPage':
        return 'Average playtime (sec)';
      case 'retentionD1':
        return 'Retention D1 (%)';
      case 'retentionD7':
        return 'Retention D7 (%)';
      case 'rating':
        return 'Rating';
      case 'revenue':
        return 'My revenue (EUR)';
      case 'users':
        return 'Total players';
      case 'returningPlayers':
        return 'Returning users (%)';
      case 'mobileUsers':
        return 'Mobile users (%)';
      case 'avgConversion':
        return 'Gameplay conversion (%)';
      case 'avgLoadingTime':
        return 'Avg loading time (sec)';
      case 'issues':
        return 'Issues / 1k plays';
      default:
        return '';
    }
  };

  const chartRawData = getChartRawData();
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const getMetricLabel = (): string => {
              switch (selectedMetric) {
                case 'pageviews':
                case 'users':
                case 'issues':
                  return `${formatAmount(context.parsed.y)}`;
                case 'avgTimeOnPage':
                  return `${formatDurationAsHMS(moment.duration(context.parsed.y, 'seconds'))}`;
                case 'retentionD1':
                case 'retentionD7':
                case 'returningPlayers':
                case 'mobileUsers':
                case 'avgConversion':
                  return `${numeral(context.parsed.y).format(PERCENTAGE_FORMAT)}%`;
                case 'rating':
                  return `${context.parsed.y.toFixed(1)}`;
                case 'revenue':
                  return `${context.parsed.y.toLocaleString('nl-BE', { style: 'currency', currency: 'EUR' })}`;
                case 'avgLoadingTime':
                  return `${i18n.number(context.parsed.y, { notation: 'compact' })} sec`;
                default:
                  return '';
              }
            };

            const metricLabel = getMetricLabel();
            return metricLabel;
          },
        },
      },
    },
    scales: {
      y: {
        title: {
          display: true,
          text: getYAxisLabel(),
        },
      },
    },
  };

  const mappedData = chartRawData.map((stats: DeveloperDailyStat | DeveloperDailyRevenueStat) => {
    switch (selectedMetric) {
      case 'pageviews':
        return (stats as DeveloperDailyStat).pageviews;
      case 'avgTimeOnPage':
        return (stats as DeveloperDailyStat).avgTimeOnPage;
      case 'retentionD1':
        return (stats as DeveloperDailyStat).retentionD1 * 100;
      case 'retentionD7':
        return (stats as DeveloperDailyStat).retentionD7 * 100;
      case 'rating':
        return null;
      case 'revenue':
        return (stats as DeveloperDailyRevenueStat).revenueShareDaily.total;
      case 'users':
        return (stats as DeveloperDailyStat).users;
      case 'returningPlayers':
        return (stats as DeveloperDailyStat).returningPlayers * 100;
      case 'avgConversion':
        return (stats as DeveloperDailyStat).avgConversion * 100;
      case 'avgLoadingTime':
        return (stats as DeveloperDailyStat).avgLoadingTime;
      case 'issues':
        return (stats as DeveloperDailyStat).issues;
      default:
        return 0;
    }
  });
  const allEmpty = mappedData.length === 0 || mappedData.every((value) => value === 0 || value === null);
  const unsupportedMetric = selectedMetric === 'rating';

  const labels = chartRawData.map((stats: DeveloperDailyStat | DeveloperDailyRevenueStat) => moment(stats.date).format('Do MMM'));
  const chartData = {
    labels,
    datasets: [
      {
        data: mappedData,
        backgroundColor: '#1BECBA',
      },
    ],
  };

  const tooltipText = timeframeFilter.period === 'ALL_TIME' ? 'The data in this chart is limited to the past year' : null;
  const notice = getNotice(chartRawData);

  return (
    <div style={{ padding: theme.spacing(2) }}>
      {notice && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography style={{ padding: '16px', margin: '0 16px', textAlign: 'justify', whiteSpace: 'pre-line' }} variant="body1">
            {notice}
          </Typography>
        </div>
      )}
      {tooltipText && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <MuiTooltip title={tooltipText}>
            <InfoIcon style={{ height: 16, width: 16, marginLeft: 4 }} />
          </MuiTooltip>
        </div>
      )}
      {allEmpty || unsupportedMetric ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography style={{ padding: '16px', textAlign: 'center' }} variant="body1">
            {unsupportedMetric ? 'No timeseries data available for this metric' : 'No data available for the selected timeframe'}
          </Typography>
        </div>
      ) : (
        <Bar options={chartOptions} data={chartData} />
      )}
    </div>
  );
};

export default DeveloperSubmissionStatsDailyBar;
